import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Icon } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getClientId, isOktaEnabled } from '@thoughtspot/system-config-service';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SamlConnectionListQuery, useGetMfaConfigLazyQuery, useSetMfaTscliCommand, useUpdateMfaConifgMutation, } from '../../../../../../services/saml/saml-service';
import { useGetLdapAdIamV1Query, useGetLocalIamV1Query, useGetSamlIamV1Query, useSetLocalIamV1Mutation, } from '../../../../../../services/system/system-service';
import { getDefaultSamlHeaders, TranslationsKeys, } from '../../../../../authentication/constants';
import { getConfigRequestFromProviderConfig, getGroupName, getMixPanelMetric, getProviderConfigFromResponse, MFA_BANNER_FEATURE_NAME, MFA_FEATURE_ID, mfaAcceptedState, OKTA_PROVIDER_OTP, OKTA_PROVIDER_PUSH, providerConfig as defaultProviderConfig, providerIdToSortOrder, tscliConfigOperationType, } from '../../../../../authentication/mfa/constants';
import styles from './local-auth-content.module.scss';
const ACTIVE = 'ACTIVE';
const LocalAuthSection = ({ onSettingChanged, onSuccess, onError, }) => {
    const { t } = useTranslation();
    const [enabled, setEnabled] = useState(false);
    const [pending, setPending] = useState(false);
    const clientId = getClientId();
    const { data: localAuthData, loading: localAuthLoading, } = useGetLocalIamV1Query();
    const { data: samlV2Response } = SamlConnectionListQuery({
        clientId,
        isSaml: true,
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const { data: oidcV2Response } = SamlConnectionListQuery({
        clientId,
        isSaml: false,
        defaultHeaders: getDefaultSamlHeaders(),
    });
    const { data: samlV1Response } = useGetSamlIamV1Query();
    const { data: ldapAdResponse } = useGetLdapAdIamV1Query();
    useEffect(() => {
        if (!localAuthLoading && localAuthData?.Data) {
            setEnabled(localAuthData.Data.status === true);
        }
    }, [localAuthData, localAuthLoading]);
    const canDisableInternalAuth = useCallback(() => {
        if (isOktaEnabled()) {
            const samlList = samlV2Response?.Data?.samlList ?? [];
            const oidcList = oidcV2Response?.Data?.samlList ?? [];
            return [...samlList, ...oidcList].some((idp) => idp?.status === ACTIVE);
        }
        const adEnabled = ldapAdResponse?.Data?.enable_ldap === true;
        const samlEnabled = samlV1Response?.Data?.enable_saml === true;
        return adEnabled || samlEnabled;
    }, [samlV2Response, oidcV2Response, samlV1Response, ldapAdResponse]);
    const [updateLocalAuth] = useSetLocalIamV1Mutation(() => {
        setPending(false);
        onSuccess?.(t(TranslationsKeys.TOAST_CHANGES_SAVED));
        try {
            collectEvent(new UserAction(userActionTypeEnum.CONFIGURE_INTERNAL_AUTH_IAM_V1), {});
        }
        catch {
        }
        onSettingChanged?.();
    }, () => {
        setPending(false);
        setEnabled(prev => !prev);
        onError?.(t('admin.error.generic'));
    }, { status: enabled });
    const handleToggle = (checked) => {
        if (pending)
            return;
        if (!checked && !canDisableInternalAuth()) {
            const msg = isOktaEnabled()
                ? t(TranslationsKeys.INTERNAL_AUTH_ERROR_OIDC_SAML_NOT_CONFIGURED)
                : t(TranslationsKeys.INTERNAL_AUTH_ERROR_AD_SAML_NOT_CONFIGURED);
            onError?.(msg);
            return;
        }
        setPending(true);
        setEnabled(checked);
        updateLocalAuth({ variables: { data: { status: checked } } });
    };
    return (React.createElement(Horizontal, { className: styles.localAuthRow, vAlignContent: "center" },
        React.createElement(Horizontal, { grow: true, vAlignContent: "center" },
            React.createElement(Typography, { variant: "body-normal", overrideTag: "span", className: styles.localAuthRowLabel, noMargin: true }, t(TranslationsKeys.SETTINGS_LOCAL_AUTH_ENABLE))),
        React.createElement(Toggle, { id: "local-auth-toggle", name: "local-auth-toggle", label: "", hideLabel: true, checked: enabled, disabled: pending || localAuthLoading, onChange: e => handleToggle(e.target.checked), className: styles.localAuthToggle, "data-testid": "local-auth-toggle" })));
};
const MfaSection = ({ onSettingChanged, onSuccess, onError, }) => {
    const { t } = useTranslation();
    const [providers, setProviders] = useState([
        ...defaultProviderConfig,
    ]);
    const lastGoodProvidersRef = useRef([
        ...defaultProviderConfig,
    ]);
    const [pending, setPending] = useState(false);
    const [showAllDisabledModal, setShowAllDisabledModal] = useState(false);
    const [allDisabledMetric, setAllDisabledMetric] = useState();
    const [bannerPostData, setBannerPostData] = useState(null);
    const { getMfaConfigInfo, MfaConfigInfo, loading: mfaLoading, error: mfaFetchError, } = useGetMfaConfigLazyQuery();
    const { updateMfaConfig, data: updateMfaResponse, loading: updateMfaLoading, error: updateMfaError, } = useUpdateMfaConifgMutation();
    const [setBannerStatus] = useSetMfaTscliCommand(MFA_BANNER_FEATURE_NAME, bannerPostData);
    useEffect(() => {
        getMfaConfigInfo({
            clientId: getClientId(),
            defaultHeaders: getDefaultSamlHeaders(),
            groupName: getGroupName(),
        });
    }, []);
    useEffect(() => {
        if (mfaLoading)
            return;
        if (mfaFetchError) {
            onError?.(t('mfa.auth.fetch.error'));
            return;
        }
        if (MfaConfigInfo) {
            const next = [...getProviderConfigFromResponse(MfaConfigInfo)];
            setProviders(next);
            lastGoodProvidersRef.current = next;
        }
    }, [MfaConfigInfo, mfaLoading, mfaFetchError]);
    useEffect(() => {
        if (updateMfaLoading)
            return;
        if (updateMfaError) {
            setPending(false);
            setProviders([...lastGoodProvidersRef.current]);
            onError?.(t('mfa.auth.update.error'));
            return;
        }
        if (updateMfaResponse?.Data) {
            const next = [
                ...getProviderConfigFromResponse(updateMfaResponse.Data),
            ];
            setProviders(next);
            lastGoodProvidersRef.current = next;
            setPending(false);
            onSettingChanged?.();
            onSuccess?.(t(TranslationsKeys.TOAST_CHANGES_SAVED));
            const allDisabled = next.every(p => !p.enabled);
            const state = allDisabled
                ? mfaAcceptedState.SHOWBANNER
                : mfaAcceptedState.ACCEPTED;
            const postData = {
                configOperation: tscliConfigOperationType.ADD,
                configOptions: [
                    {
                        optionKey: MFA_FEATURE_ID,
                        optionValue: btoa(String(state)),
                        optionLabel: t('mfa.auth.title', { lng: 'en-US' }),
                    },
                ],
            };
            setBannerPostData(postData);
            setBannerStatus({
                variables: {
                    featureName: MFA_BANNER_FEATURE_NAME,
                    params: postData,
                },
            });
        }
    }, [updateMfaResponse, updateMfaLoading, updateMfaError]);
    const isToggleActionValid = useCallback((next) => {
        const otpIdx = providerIdToSortOrder[OKTA_PROVIDER_OTP];
        const pushIdx = providerIdToSortOrder[OKTA_PROVIDER_PUSH];
        const otpWasEnabled = providers[otpIdx]?.enabled;
        const otpNowDisabled = !next[otpIdx]?.enabled;
        if (otpWasEnabled && otpNowDisabled) {
            return !next[pushIdx]?.enabled;
        }
        return true;
    }, [providers]);
    const applyConfig = useCallback((next, metric) => {
        setPending(true);
        setProviders(next);
        updateMfaConfig(getConfigRequestFromProviderConfig(next));
        if (metric) {
            try {
                collectEvent(metric, {});
            }
            catch {
            }
        }
    }, [updateMfaConfig]);
    const handleProviderToggle = (index, checked) => {
        if (pending)
            return;
        const next = providers.map((p, i) => i === index ? { ...p, enabled: checked } : p);
        const metric = getMixPanelMetric(checked, next[index].id);
        if (!isToggleActionValid(next)) {
            onError?.(t('mfa.auth.push.disable.error'));
            return;
        }
        const willAllDisable = next.every(p => !p.enabled);
        if (willAllDisable) {
            setAllDisabledMetric(metric);
            setShowAllDisabledModal(true);
            return;
        }
        applyConfig(next, metric);
    };
    const confirmDisableAll = () => {
        const next = providers.map(p => ({ ...p, enabled: false }));
        applyConfig(next, allDisabledMetric);
        setShowAllDisabledModal(false);
        setAllDisabledMetric(undefined);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Typography, { variant: "h4", className: styles.localAuthSectionTitle, noMargin: true }, t(TranslationsKeys.SETTINGS_LOCAL_AUTH_METHODS)),
        providers.map((provider, index) => (React.createElement(Horizontal, { key: provider.id, className: styles.localAuthRow, vAlignContent: "center" },
            React.createElement(Horizontal, { grow: true, vAlignContent: "center", className: styles.providerInfo },
                React.createElement(Icon, { id: provider.icon, className: styles.providerIcon }),
                React.createElement(Vertical, { className: styles.providerText },
                    React.createElement(Typography, { variant: "body-normal", overrideTag: "span", className: styles.localAuthRowLabel, noMargin: true }, t(provider.name)),
                    React.createElement(Typography, { variant: "caption", overrideTag: "span", className: styles.providerDescription, noMargin: true }, t(provider.description)))),
            React.createElement(Toggle, { id: `provider-toggle-${provider.id}`, name: `provider-toggle-${provider.id}`, label: "", hideLabel: true, checked: provider.enabled, disabled: pending || mfaLoading, onChange: e => handleProviderToggle(index, e.target.checked), className: styles.localAuthToggle, "data-testid": `provider-toggle-${provider.id}` })))),
        React.createElement(Modal, { isOpen: showAllDisabledModal, size: ModalSize.SMALL, title: t('mfa.auth.modal.title'), confirmButton: {
                text: t('mfa.auth.modal.disbale'),
                onConfirm: confirmDisableAll,
            }, dismissButton: {
                text: t('mfa.auth.modal.cancel'),
            }, onDismiss: () => {
                setShowAllDisabledModal(false);
                setAllDisabledMetric(undefined);
            }, doNotDismissOnOutsideClickOrEsc: true, "data-testid": "mfa-disable-all-modal" },
            React.createElement(Vertical, null,
                React.createElement(Typography, { "data-testid": "mfa-disablement-dialog", variant: "body-normal", color: Colors.gray }, t('mfa.auth.disable.warning'))))));
};
export const LocalAuthContent = ({ view = 'localAuth', onSettingChanged, onSuccess, }) => {
    const [errorBanner, setErrorBanner] = useState(null);
    useEffect(() => setErrorBanner(null), [view]);
    return (React.createElement(Vertical, { className: styles.localAuthContainer },
        errorBanner && (React.createElement(Alert.Banner, { isVisible: true, message: errorBanner, type: BannerAlertType.ERROR, onClose: () => setErrorBanner(null), showCloseButton: true })),
        view === 'localAuth' ? (React.createElement(LocalAuthSection, { onSettingChanged: onSettingChanged, onSuccess: onSuccess, onError: msg => setErrorBanner(msg) })) : (React.createElement(MfaSection, { onSettingChanged: onSettingChanged, onSuccess: onSuccess, onError: msg => setErrorBanner(msg) }))));
};
//# sourceMappingURL=local-auth-content.container.js.map